import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";

const EMPTY = { question_es: "", question_en: "", answer_es: "", answer_en: "", order: 0 };

export default function AdminFaqs() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/faqs").then((r) => setFaqs(r.data.faqs)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: faqs.length + 1 }); setDialogOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...EMPTY, ...f }); setDialogOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      question_es: form.question_es, question_en: form.question_en,
      answer_es: form.answer_es, answer_en: form.answer_en,
      order: parseInt(form.order) || 0,
    };
    try {
      if (editing) await api.put(`/admin/faqs/${editing.id}`, payload);
      else await api.post("/admin/faqs", payload);
      toast.success(editing ? (es ? "Pregunta actualizada" : "FAQ updated") : (es ? "Pregunta creada" : "FAQ created"));
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    await api.delete(`/admin/faqs/${deleteId}`);
    toast.success(es ? "Pregunta eliminada" : "FAQ deleted");
    setDeleteId(null); load();
  };

  return (
    <div data-testid="admin-faqs">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light">{es ? "Contenido · FAQs" : "Content · FAQs"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{faqs.length} {es ? "preguntas" : "questions"}</p>
        </div>
        <Button className="rounded-none" onClick={openNew} data-testid="add-faq-btn"><Plus className="h-4 w-4 mr-1" /> {es ? "Añadir pregunta" : "Add question"}</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium overline w-12">#</th>
                <th className="p-4 font-medium overline">{es ? "Pregunta" : "Question"}</th>
                <th className="p-4 font-medium overline text-right">{es ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {faqs.map((f) => (
                <tr key={f.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`faq-row-${f.id}`}>
                  <td className="p-4 text-muted-foreground">{f.order}</td>
                  <td className="p-4 font-medium">{f[`question_${lang}`] || f.question_es}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(f.id)} className="hover:text-destructive" data-testid={`delete-faq-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? (es ? "Editar pregunta" : "Edit FAQ") : (es ? "Nueva pregunta" : "New FAQ")}</DialogTitle>
            <DialogDescription className="sr-only">FAQ form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label className="overline text-muted-foreground">Pregunta (ES)</Label><Input required value={form.question_es} onChange={set("question_es")} className="rounded-none mt-1.5" data-testid="ff-question-es" /></div>
            <div><Label className="overline text-muted-foreground">Question (EN)</Label><Input required value={form.question_en} onChange={set("question_en")} className="rounded-none mt-1.5" data-testid="ff-question-en" /></div>
            <div><Label className="overline text-muted-foreground">Respuesta (ES) — {es ? "usa \"- \" al inicio de línea para listas" : "use \"- \" at line start for lists"}</Label><Textarea rows={5} required value={form.answer_es} onChange={set("answer_es")} className="rounded-none mt-1.5 resize-none" data-testid="ff-answer-es" /></div>
            <div><Label className="overline text-muted-foreground">Answer (EN)</Label><Textarea rows={5} required value={form.answer_en} onChange={set("answer_en")} className="rounded-none mt-1.5 resize-none" data-testid="ff-answer-en" /></div>
            <div><Label className="overline text-muted-foreground">{es ? "Orden" : "Order"}</Label><Input type="number" value={form.order} onChange={set("order")} className="rounded-none mt-1.5 max-w-[120px]" data-testid="ff-order" /></div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="rounded-none flex-1" disabled={saving} data-testid="save-faq-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? (es ? "Guardar cambios" : "Save changes") : (es ? "Crear pregunta" : "Create FAQ"))}</Button>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>{es ? "Cancelar" : "Cancel"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>{es ? "¿Eliminar esta pregunta?" : "Delete this FAQ?"}</AlertDialogTitle><AlertDialogDescription>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{es ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-faq">{es ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
